import { toMonthValue } from "@artha/ui";
import { Link2 } from "lucide-react";
import type { CandidateEditableProfileDto } from "@artha/api-contracts";
import type { ProfileHistoryEntry, ProfileHistoryKind, ProfileHistoryGroup } from "./profileCards.types";
import {
  PROFILE_CARD_DATE_PATTERN,
  PROFILE_CURRENT_DATE_PATTERN,
  PROFILE_YEAR_PATTERN,
  PROFILE_LINK_LABELS,
  PROFILE_LINK_ICONS,
  PROFILE_CARD_HTTP_PATTERN,
  PROFILE_EMPLOYMENT_TYPES,
  PROFILE_DEFAULT_EMPLOYMENT_TYPE,
} from "./profileCards.constants";

export function profileLinkIcon(value: string) {
  const url = safeProfileLink(value);
  if (!url) return Link2;
  const host = new URL(url).hostname.replace(/^www\./i, "");
  return PROFILE_LINK_ICONS[host] || Link2;
}

export function formatProfileDate(value?: string) {
  if (!value) return "";
  const match = PROFILE_CARD_DATE_PATTERN.exec(value);
  if (!match || Number(match[2]) < 1 || Number(match[2]) > 12) return value;
  return new Intl.DateTimeFormat("en", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)));
}

export function profilePeriod(entry: ProfileHistoryEntry) {
  return [
    formatProfileDate(entry.startDate),
    isCurrentHistoryEntry(entry) ? "Present" : formatProfileDate(entry.endDate),
  ]
    .filter(Boolean)
    .join(" - ");
}

export function splitProfileChips(value: string) {
  const seen = new Set<string>();
  return value
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter((item) => {
      const key = item.toLowerCase();
      if (!item || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

export function safeProfileLink(value?: string) {
  if (!value || !PROFILE_CARD_HTTP_PATTERN.test(value)) return undefined;
  try {
    const url = new URL(value);
    return url.username || url.password ? undefined : url.href;
  } catch {
    return undefined;
  }
}

function historyDateOrder(value?: string) {
  const input = value?.trim() || "";
  const month = toMonthValue(input);
  if (month) return Number(month.replace("-", ""));
  return PROFILE_YEAR_PATTERN.test(input) && Number(input) > 0 ? Number(input) * 100 : 0;
}

export function isCurrentHistoryEntry(entry: ProfileHistoryEntry) {
  return entry.isCurrent === true || PROFILE_CURRENT_DATE_PATTERN.test(entry.endDate?.trim() || "");
}

export function profileHistoryGroups(entries: ProfileHistoryEntry[], kind: ProfileHistoryKind): ProfileHistoryGroup[] {
  const ordered = entries.map((entry, index) => ({ entry, index }));
  if (kind !== "projects") ordered.sort((left, right) =>
    Number(isCurrentHistoryEntry(right.entry)) - Number(isCurrentHistoryEntry(left.entry)) ||
    (isCurrentHistoryEntry(right.entry) ? 0 : historyDateOrder(right.entry.endDate)) -
      (isCurrentHistoryEntry(left.entry) ? 0 : historyDateOrder(left.entry.endDate)) ||
    historyDateOrder(right.entry.startDate) - historyDateOrder(left.entry.startDate) ||
    left.index - right.index,
  );
  const groups: ProfileHistoryGroup[] = [];
  const companies = new Map<string, ProfileHistoryGroup>();
  for (const item of ordered) {
    const company = kind === "experience" ? item.entry.company?.trim().replace(/\s+/g, " ") : undefined;
    const key = company?.toLocaleLowerCase();
    const existing = key ? companies.get(key) : undefined;
    if (existing) existing.entries.push(item);
    else {
      const group = { company, entries: [item] };
      groups.push(group);
      if (key) companies.set(key, group);
    }
  }
  return groups;
}

export function profileLatestRole(profile: Pick<CandidateEditableProfileDto, "experience" | "titles">) {
  const roles = profile.experience?.filter((entry) => entry.title?.trim()) || [];
  return profileHistoryGroups(roles, "experience")[0]?.entries[0].entry.title?.trim() ||
    profile.titles.find((title) => title.trim())?.trim() || "";
}

export function profileEmploymentType(value?: string) {
  return PROFILE_EMPLOYMENT_TYPES.find((type) => type.toLowerCase() === value?.trim().toLowerCase()) ||
    value?.trim() || PROFILE_DEFAULT_EMPLOYMENT_TYPE;
}

export function profileLinkLabel(value: string) {
  const url = safeProfileLink(value);
  if (!url) return "Website";
  const host = new URL(url).hostname.replace(/^www\./i, "");
  return PROFILE_LINK_LABELS[host] || host;
}
